// client/src/pages/TicketDistributor/index.jsx
import React, { useState, useEffect } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useSocket } from '../../contexts/SocketContext';
import ticketService from '../../services/ticketService';
import TicketPrinter from '../../components/TicketPrinter';
import LoginModal from '../../components/LoginModal';
import BandeauDefilant from '../../components/BandeauDefilant';
import useThemeCSS from '../../hooks/useThemeCSS';
import { orderedServiceLangs } from '../../utils/serviceNameLangs';
import axios from 'axios';

const TicketDistributor = () => {
  const { config, loading, error } = useConfig();
  const { socket, connected, registerClient } = useSocket();
  
  // Charger les styles CSS du distributeur
  useThemeCSS('ticket-distributor.css');
  
  // États - TOUS LES HOOKS DOIVENT ÊTRE DÉCLARÉS EN PREMIER
  const [currentTicket, setCurrentTicket] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [currentDate] = useState(new Date());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(null);
  const [isDistributorClosed, setIsDistributorClosed] = useState(true); // Fermé par défaut, ouvert seulement si connecté

  // Vérifier l'authentification au montage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      // Vérifier si le token est valide
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
      setIsDistributorClosed(false); // Ouvrir le distributeur quand l'utilisateur est connecté
    } else {
      setIsDistributorClosed(true); // Fermer le distributeur si pas connecté
    }
  }, []);

  // Enregistrer comme client "distributeur" au montage
  useEffect(() => {
    if (socket && connected && isAuthenticated) {
      registerClient('distributor');
    }
  }, [socket, connected, registerClient, isAuthenticated]);

  // Écouter les déconnexions forcées depuis l'admin
  useEffect(() => {
    if (socket && connected) {
      socket.on('force-logout-distributor', () => {
        // Déconnecter l'utilisateur
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setUser(null);
        setIsDistributorClosed(true); // Fermer le distributeur après déconnexion forcée
        // Afficher un message à l'utilisateur
        setTimeout(() => {
          alert('Session fermée par l\'administrateur.\nVeuillez vous reconnecter si nécessaire.');
        }, 100);
      });

      return () => {
        socket.off('force-logout-distributor');
      };
    }
  }, [socket, connected]);

  // MAINTENANT LES RETURNS CONDITIONNELS APRÈS TOUS LES HOOKS
  // Si les données sont en cours de chargement
  if (loading) {
    return (
      <div className="distributor-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="p-8 bg-white rounded-lg shadow-md text-center">
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-700">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  // Si une erreur s'est produite
  if (error || !config) {
    return (
      <div className="distributor-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold mb-4">Erreur</h1>
            <p className="mb-6 text-gray-700">{error || "La configuration n'a pas pu être chargée"}</p>
            <button 
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
              onClick={() => window.location.reload()}
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fonction pour gérer la connexion réussie
  const handleLoginSuccess = (loginData) => {
    setIsAuthenticated(true);
    setUser(loginData.user);
    setShowLoginModal(false);
    setIsDistributorClosed(false); // Ouvrir le distributeur après connexion
  };

  // Fonction pour se déconnecter
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    setIsDistributorClosed(true); // Fermer le distributeur après déconnexion
  };

  // Fonction pour gérer le clic sur la page (demander connexion)
  const handlePageClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      // Faire disparaître le modal après 10 secondes si pas d'interaction
      setTimeout(() => {
        setShowLoginModal(false);
      }, 10000);
    }
  };

  // Fonction pour demander un nouveau ticket
  const handleTicketRequest = async (serviceType) => {
    // Vérifier l'authentification
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    try {
      // Simuler l'impression
      setPrinting(true);
      
      // Appeler l'API pour créer un nouveau ticket
      const response = await ticketService.createTicket(serviceType);
      
      if (response.success && response.data) {
        // Formater les données du ticket pour l'affichage et l'impression
        const ticket = response.data;
        const service = config.serviceTypes.find(s => s.id === ticket.serviceType);
        
        const formattedTicket = {
          id: ticket.ticketId,
          number: ticket.number,
          serviceType: ticket.serviceType,
          serviceName: service?.name || 'Service',
          serviceNameAr: service?.nameAr || '',
          serviceNameEn: service?.nameEn || '',
          timestamp: ticket.createdAt,
          formattedTime: new Date(ticket.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
          // Champs BRUTS du serveur, requis par @mostajs/ticket-print pour un ticket
          // identique à celui du distributeur Electron. Sans eux, l'impression retombait
          // sur la clé technique et perdait numéro global, position, QR et libellé.
          ticketId: ticket.ticketId,
          label: ticket.label,
          globalNumber: ticket.globalNumber,
          waitingAhead: ticket.waitingAhead,
          serverName: ticket.serverName,
          numbering: ticket.numbering,
          languages: ticket.languages,
        };
        
        setCurrentTicket(formattedTicket);

        // L'état d'impression sera réinitialisé par le composant d'impression
        setTimeout(() => {
          setPrinting(false);
        }, 500);

        // Retour automatique à la liste des services après la durée configurée
        // (config.distributor.ticketDisplayDuration, en secondes ; défaut 6 s).
        const displaySec = Number(config?.distributor?.ticketDisplayDuration) || 6;
        setTimeout(() => {
          setCurrentTicket(null);
        }, displaySec * 1000);
      } else {
        setPrinting(false);
        alert("Erreur lors de la création du ticket");
      }
    } catch (err) {
      console.error('Erreur lors de la demande de ticket:', err);
      setPrinting(false);
      alert("Erreur lors de la création du ticket");
    }
  };

  /**
   * Ordre, visibilité et échelle des langues du nom de service — LUS DANS `styles.display`,
   * comme le fait le distributeur Electron (apps/distributor/renderer/app.js). La borne et
   * l'écran d'attente présentent ainsi le même service de la même façon, et un site qui a
   * choisi « arabe d'abord, anglais masqué » en console l'obtient sur les deux.
   *
   * Auparavant l'ordre était écrit en dur (français puis arabe) et `nameEn` n'était jamais
   * affiché : les réglages de langue de la console n'avaient aucune prise sur la borne.
   */
  const languesService = orderedServiceLangs(config.styles?.display, 'service-name');
  // La description suit la MÊME langue et le même ordre que le nom : une fiche qui nomme
  // le service en arabe puis le décrit en français seulement n'est lisible par personne.
  const descriptionParLangue = {
    ar: (s) => s.descriptionAr,
    en: (s) => s.descriptionEn,
    fr: (s) => s.description,
  };

  // Format de la date
  const dateStr = currentDate.toLocaleDateString('fr-FR', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="distributor-panel distributor-container" onClick={handlePageClick}>
      {/* En-tête */}
      <header className="distributor-header">
        <div className="distributor-header-content">
          <div className="distributor-logo-container">
            <img 
              src={config.agency?.logo || '/logo.png'} 
              alt={config.agency?.name || 'Logo'} 
              className="distributor-logo"
              onError={(e) => {
                e.target.src = '/logo.png'; // Fallback si l'image ne charge pas
                e.target.onerror = null;
              }}
            />
            <div className="distributor-info">
              <h1 className="distributor-title">{config.agency?.company || 'Entreprise'} - {config.agency?.name || 'Agence'}</h1>
              <p className="distributor-subtitle">Système de Gestion de File d'Attente</p>
            </div>
          </div>
          
          <div className="distributor-datetime">
            <p className="text-lg">{dateStr}</p>
          </div>
        </div>
      </header>

      {/*
        Bandelette défilante — DEUX emplacements possibles, posés tous les deux ici, et c'est
        le réglage (`distributor.ticker.position`) qui décide lequel s'affiche : le composant
        rend `null` à l'emplacement qu'on ne lui a pas demandé. Écrire la condition dans la
        page aurait dispersé le même choix à deux endroits.
        Rien n'est rendu tant que la bandelette n'est pas activée et renseignée.
      */}
      <BandeauDefilant config={config} position="top" />

      {/* Contenu principal */}
      <main className="distributor-main">
        {/* Message de bienvenue */}
        <div className="welcome-panel">
          <h2 className="welcome-title-ar rtl" dir="rtl">{config.ui?.welcomeMessage?.ar || "مرحبا بكم في الوكالة التجارية"}</h2>
          <h2 className="welcome-title-fr">{config.ui?.welcomeMessage?.fr || "Bienvenue à l'agence commerciale"}</h2>
        </div>

        {currentTicket ? (
          // Affichage et impression du ticket
          <div className="ticket-display">
            <h3 className="ticket-title">Votre ticket</h3>
            {/* Libellé COMPOSÉ par le serveur (« سحب النتائج 19 »), pas la clé technique
                (« PED19 ») : la même valeur que le ticket imprimé, /display et la voix. */}
            <div className="ticket-number">
              {currentTicket.label || currentTicket.id}
            </div>
            <div className="ticket-service">
              {currentTicket.serviceName}
            </div>
            <div className="ticket-service-ar rtl" dir="rtl">
              {currentTicket.serviceNameAr}
            </div>
            <div className="ticket-time">
              {currentTicket.formattedTime}
            </div>
            <div className="ticket-instruction">
              Veuillez attendre l'appel de votre numéro
            </div>
            
            {/* Composant d'impression de ticket */}
            <TicketPrinter 
              ticket={currentTicket} 
              onPrintComplete={() => setPrinting(false)}
              autoprint={true}
            />
          </div>
        ) : printing ? (
          // Animation d'impression
          <div className="loading-container">
            <h3 className="loading-title">Impression en cours...</h3>
            <div className="loading-spinner"></div>
            <p className="loading-message">Veuillez patienter...</p>
          </div>
        ) : (
          // Boutons de sélection de service
          <div className="service-selection">
            <h3 className="service-title">Sélectionnez votre service</h3>
            
            <div className="service-buttons">
              {config.serviceTypes?.map((service) => (
                <button
                  key={service.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTicketRequest(service.id);
                  }}
                  className="service-button"
                  style={{ "--service-color": service.color || 'var(--distributor-primary-color)' }}
                >
                  <div className="service-info">
                    {languesService.map((l) => {
                      const nom = l.text(service);
                      return nom ? <h3 key={l.key} className={l.cls} dir={l.dir}>{nom}</h3> : null;
                    })}
                    {languesService.map((l) => {
                      const desc = descriptionParLangue[l.key]?.(service);
                      return desc
                        ? <p key={l.key} className={`service-description${l.dir === 'rtl' ? ' rtl' : ''}`} dir={l.dir}>{desc}</p>
                        : null;
                    })}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      <BandeauDefilant config={config} position="bottom" />

      {/* Pied de page */}
      <footer className="distributor-footer">
        <div className="distributor-footer-top">
          <div className="accessibility-icons">
            <span>♿</span>
            <span>👵</span>
            <span>🧍</span>
            <span>🔄</span>
          </div>
          <div className="footer-instruction">
            {config.ui?.ticketInstruction?.fr || "PRENEZ VOTRE TICKET"}
            <span className="mx-2">-</span>
            <span className="rtl" dir="rtl">{config.ui?.ticketInstruction?.ar || "خذ تذكرتك"}</span>
          </div>
          <div className="footer-contact">
            <p className="text-sm">{config.agency?.companySlogan || ""}</p>
          </div>
        </div>
        <div className="distributor-footer-bottom">
          {config.agency?.companySloganFr || ""}
          <span className="mx-2">-</span>
          <span className="rtl" dir="rtl">{config.agency?.companySlogan || "دائما أقرب"}</span>
        </div>
      </footer>

      {/* Modal de connexion */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Modal de fermeture du distributeur */}
      {isDistributorClosed && (
        <div className="closed-modal-overlay">
          <div className="closed-modal">
            <div className="closed-icon">🚫</div>
            <h3 className="closed-title">Guichet Fermé</h3>
            <h3 className="closed-title-ar rtl" dir="rtl">الشباك مغلق</h3>
            <div className="closed-info">
              <p className="closed-message">Le distributeur de tickets est actuellement fermé</p>
              <p className="closed-message-ar rtl" dir="rtl">موزع التذاكر مغلق حاليا</p>
            </div>
            <div className="closed-instruction">
              <p>Veuillez revenir plus tard</p>
              <p className="rtl" dir="rtl">يرجى العودة لاحقا</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Créer une div invisible pour stocker l'ID de station - pour le débogage */}
      <div 
        id="station-id-debug" 
        className="hidden"
        data-station-id={localStorage.getItem('ticketflow-station-id') || ''}
      ></div>
    </div>
  );
};

export default TicketDistributor;